import React, { FC, useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Modal from 'react-bootstrap/Modal';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useWizardContext } from 'store/wizard';
import { useWorkModeContext } from 'store/work-mode';
import { useActiveClientContext } from 'store/active-client';

import setServerErrors from 'utils/helpers/formErrors';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { getUserInfo } from 'utils/auth';

import Form from 'components/form/Form';
import Button from 'components/Button';
import Wizard from '../../components/Wizard';
import WizardNavAndSubmit from '../../components/WizardNavAndSubmit';
import buildWizardProps from '../../helpers';
import UserTable from './UserTable';
import DocumentPreview from './DocumentPreview';
import { IInputs } from './types';

interface IProps {
  data?: any;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
}

const StepFour: FC<IProps> = ({ data }: IProps) => {
  const {
    creator_name: creatorName,
    creator_name_en: creatorNameEn,
    name,
    name_en: nameEn,
    number,
    rendered_contents: content,
    rendered_contents_en: contentEn,
    is_multi_user: isMassDoc,
    is_multilingual: isMultilingual,
  } = data;
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [isDocVisible, setIsDocVisible] = useState(false);
  const [relatedUsers, setRelatedUsers] = useState<User[]>([]);
  const [documentId, setDocumentId] = useState<number | null>(null);

  const history = useHistory();
  const { client } = useActiveClientContext();
  const queryClient = useQueryClient();
  const params = useParams<{ id: string | undefined }>();
  const { workMode } = useWorkModeContext();
  const isEmployeeMode = workMode === 'employee';
  const { isCandidate } = getUserInfo();
  const canEdit = !isEmployeeMode && !isCandidate;

  // Flag to recognize doc mode - edit or create
  const holderId = params.id || '';

  const { currentStep, setCurrentStep, totalSteps } = useWizardContext();

  const buildDefaultValues = () => {
    const obj: any = {
      rendered_contents: content,
      ...(isMultilingual ? { rendered_contents_en: contentEn } : {}),
    };
    return obj;
  };

  const handleEdit = (userId: string) => {
    API.get(`document-wizard/step-four/multi-doc-separated/${userId}`)
      .then(res => {
        const {
          document_id: documentID,
          rendered_contents: renderedContents,
          rendered_contents_en: renderedContentsEn,
        } = res.data;
        setDocumentId(documentID);
        setValue('rendered_contents', renderedContents);
        setIsDocVisible(true);
        setEditUserId(userId);
        if (isMultilingual) setValue('rendered_contents_en', renderedContentsEn);
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, setError));
  };

  const handleEditSubmit = () => {
    if (relatedUsers.length === 0) {
      return;
    }

    // Get the form data
    const values = methods.getValues();

    return API.patch(`document-wizard/step-four/multi-doc-separated/${editUserId}`, {
      rendered_contents: values.rendered_contents,
      ...(isMultilingual ? { rendered_contents_en: values.rendered_contents_en } : {}),
    })
      .then(res => {
        setIsEditMode(false); // Exit edit mode after successful update
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, setError));
  };

  const methods = useForm<IInputs>({
    defaultValues: buildDefaultValues(),
  });
  const { formState, watch, setError, setValue, reset } = methods;
  const { isSubmitting } = formState;

  useEffect(() => {
    if (data.related_users) {
      setRelatedUsers(data.related_users);
    }
  }, [data]);

  useEffect(() => {
    reset(buildDefaultValues());
  }, [data]);

  const renderedContents = watch('rendered_contents');
  const renderedContentsEn = watch('rendered_contents_en');

  const onSubmit = (submitData: IInputs) => {
    if (relatedUsers.length > 0) {
      setCurrentStep(5);
      const url = buildClientUrl(`/documents-wizard/step-5/${holderId}`, client);
      queryClient.removeQueries(buildClientEntity('step-four', client));
      queryClient.removeQueries(buildClientEntity('step-five', client));
      history.push({
        pathname: url,
        state: { relatedUsersCount: relatedUsers.length },
      });
      return;
    }
    return API.post(`document-wizard/step-four/${holderId}`, {
      rendered_contents: submitData.rendered_contents,
      ...(isMultilingual ? { rendered_contents_en: submitData.rendered_contents_en } : {}),
    })
      .then(res => {
        const { next_step: nextStep } = res.data;
        // Set next current step into wizard context
        setCurrentStep(nextStep);
        // Push to next step
        const url = buildClientUrl(`/documents-wizard/step-${nextStep}/${holderId}`, client);
        queryClient.removeQueries(buildClientEntity('step-four', client));
        queryClient.removeQueries(buildClientEntity('step-five', client));
        history.push(url);
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, setError));
  };

  return (
    <>
      <Wizard {...buildWizardProps(false, currentStep, 0, totalSteps)} />
      <Row className="mt-4">
        <Col md={12}>
          <Form
            methods={methods}
            onSubmit={onSubmit}
            btnLabel={t('common.saveAndContinue')}
            isHorizontal
            hideCancelBtn
            areBtnsCentered
            isSubmitDisabled={isEditMode}
          >
            <WizardNavAndSubmit
              currentStep={currentStep}
              backStep={3}
              isSubmitting={isSubmitting}
              isSubmitDisabled={isEditMode}
            />
            <hr />
            {relatedUsers.length > 0 && (
              <>
                <UserTable
                  users={relatedUsers}
                  handleEdit={handleEdit}
                />
                <hr />
              </>
            )}
            {relatedUsers.length === 0 && (
              <DocumentPreview
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                renderedContents={renderedContents}
                renderedContentsEn={renderedContentsEn}
                handleEditSubmit={handleEditSubmit}
                name={name}
                number={documentId || number}
                isMassDoc={isMassDoc}
                isMultilingual={isMultilingual}
                creatorName={creatorName}
                creatorNameEn={creatorNameEn}
                content={content}
                contentEn={contentEn}
                setValue={setValue}
                canEdit={canEdit}
              />
            )}

            <Modal
              show={isDocVisible}
              onHide={() => setIsDocVisible(false)}
              size="xl"
            >
              <Modal.Header closeButton>
                <Modal.Title>{t('docWizard.step4.description')}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <DocumentPreview
                  isEditMode={isEditMode}
                  setIsEditMode={setIsEditMode}
                  renderedContents={renderedContents}
                  renderedContentsEn={renderedContentsEn}
                  handleEditSubmit={handleEditSubmit}
                  name={name}
                  number={documentId || number}
                  isMassDoc={isMassDoc}
                  isMultilingual={isMultilingual}
                  creatorName={creatorName}
                  creatorNameEn={creatorNameEn}
                  content={content}
                  contentEn={contentEn}
                  setValue={setValue}
                  canEdit={canEdit}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button onClick={() => setIsDocVisible(false)}>{t('docWizard.step4.close')}</Button>
              </Modal.Footer>
            </Modal>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default StepFour;
